<template>
  <div class="harmonic-range-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$(AB, CD) = -1$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage light-stage">
        <div class="hud-panel">
          <div class="hud-item blue">
            <label>交比 (Cross Ratio)</label>
            <span class="val">-1.00</span>
          </div>
          <div class="hud-item orange">
            <label>调和共轭</label>
            <span class="val">C, D 关于 A, B 调和</span>
          </div>
        </div>

        <svg width="100%" height="220" viewBox="0 0 600 220" preserveAspectRatio="xMidYMid meet">
          <!-- 基准直线 -->
          <line x1="50" y1="110" x2="550" y2="110" stroke="#cbd5e1" stroke-width="2" stroke-dasharray="4,4" />
          
          <!-- 投影辅助线 (几何背景) -->
          <path :d="`M 300,20 L ${pts.A},110 M 300,20 L ${pts.B},110 M 300,20 L ${pts.C},110 M 300,20 L ${pts.D},110`" 
                stroke="#e2e8f0" stroke-width="1" fill="none" />
          <circle cx="300" cy="20" r="4" fill="#64748b" />
          <text x="300" y="15" text-anchor="middle" font-size="10" fill="#94a3b8">投影中心 P</text>

          <!-- 调和点列主体 -->
          <g class="points-layer">
            <!-- A 点 (固定) -->
            <circle :cx="pts.A" cy="110" r="6" fill="#3b82f6" />
            <text :x="pts.A" y="135" text-anchor="middle" font-size="14" font-weight="bold" fill="#1e40af">A</text>

            <!-- B 点 (固定) -->
            <circle :cx="pts.B" cy="110" r="6" fill="#3b82f6" />
            <text :x="pts.B" y="135" text-anchor="middle" font-size="14" font-weight="bold" fill="#1e40af">B</text>

            <!-- C 点 (可动) -->
            <circle :cx="pts.C" cy="110" r="8" fill="#f59e0b" style="cursor: ew-resize;" @mousedown="startDrag('C')" />
            <text :x="pts.C" y="135" text-anchor="middle" font-size="14" font-weight="bold" fill="#b45309">C (动点)</text>

            <!-- D 点 (自动计算) -->
            <circle :cx="pts.D" cy="110" r="8" fill="#10b981" />
            <text :x="pts.D" y="135" text-anchor="middle" font-size="14" font-weight="bold" fill="#065f46">D (共轭点)</text>
          </g>

          <!-- 比例尺标注 -->
          <path :d="`M ${pts.A},155 L ${pts.C},155`" stroke="#3b82f6" stroke-width="1" />
          <text :x="(pts.A+pts.C)/2" y="170" text-anchor="middle" font-size="10" fill="#3b82f6">AC</text>
        </svg>

        <div class="physics-caption">
          <strong>几何规律：</strong>若 A, B, C, D 构成调和点列，则满足分比 AC/CB = -AD/DB。在视觉演示中，当你左右拖动点 C 时，点 D 会自动寻找其调和共轭位置。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="label-row">
             <span class="label">调节点 C 的位置</span>
             <el-slider v-model="dragX" :min="pts.A + 5" :max="pts.B - 5" :step="1" @input="updateFromSlider" />
           </div>
        </div>
        
        <div class="discovery-card-discovery-tir" v-latex>
           <div class="icon-wrap"><i class="el-icon-view"></i></div>
           <div class="text-content">
             <p><strong>数学美感：</strong>调和点列是射影几何的不变量。<strong>解惑：</strong>为什么图中的点 D 会随 C 剧烈移动？<strong>观察：</strong>当 C 靠近 AB 的中点时，D 会趋向无穷远；当 C 与 A 重合时，D 也与 A 重合。这展示了欧氏几何在射影视角下的完美对称。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'HarmonicRangeViz',
  data() {
    return {
      pts: { A: 100, B: 400, C: 200, D: 0 },
      dragX: 200,
      isDragging: false
    }
  },
  mounted() {
    this.calculateD();
  },
  methods: {
    calculateD() {
      // 调和点列公式: (AC/CB) / (AD/DB) = -1
      // 即: AC/CB = -AD/DB
      // AC = C - A, CB = B - C, AD = D - A, DB = B - D
      // (C-A)/(B-C) = -(D-A)/(B-D)
      // 令 r = (C-A)/(B-C)
      // r = (A-D)/(B-D) => rB - rD = A - D => D(1-r) = A - rB => D = (A - rB) / (1 - r)
      const r = (this.pts.C - this.pts.A) / (this.pts.B - this.pts.C);
      this.pts.D = (this.pts.A + r * this.pts.B) / (1 + r);
      
      // 注意：调和点列中，如果 C 在 AB 内部，D 必在 AB 外部
      // 重新计算调和分点的外部形式
      const AC = this.pts.C - this.pts.A;
      const CB = this.pts.B - this.pts.C;
      if (Math.abs(AC - CB) < 0.1) {
         this.pts.D = 9000; // Infinity representation in viewport
      } else {
         const ratio = AC / CB;
         this.pts.D = (this.pts.A - ratio * this.pts.B) / (1 - ratio);
      }
    },
    startDrag(type) {
      this.isDragging = true;
      window.addEventListener('mousemove', this.doDrag);
      window.addEventListener('mouseup', this.stopDrag);
    },
    doDrag(e) {
      if (!this.isDragging) return;
      const svg = document.querySelector('svg');
      const CTI = svg.getScreenCTM().inverse();
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      const svgP = pt.matrixTransform(CTI);
      
      let newC = Math.max(this.pts.A + 5, Math.min(this.pts.B - 5, svgP.x));
      this.pts.C = newC;
      this.dragX = newC;
      this.calculateD();
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener('mousemove', this.doDrag);
      window.removeEventListener('mouseup', this.stopDrag);
    },
    updateFromSlider() {
      this.pts.C = this.dragX;
      this.calculateD();
    }
  },
  beforeDestroy() {
    this.stopDrag();
  }
}
</script>

<style scoped lang="scss">
@import "./PhysicsCommon.scss";
.harmonic-range-viz { flex: 1; display: flex; flex-direction: column; height: 100%; overflow: hidden; }
.light-stage { background: #fdfdfd; border-radius: 8px; position: relative; }
</style>
